import { type HTMLAttributes } from 'react';
import { cn } from '../../utils/cn';

export type TierBadgeTier = 'tier_1' | 'tier_2' | 'tier_3';
export type TierBadgeSize = 'sm' | 'md';

export interface TierBadgeProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
  readonly tier: TierBadgeTier;
  readonly size?: TierBadgeSize;
  /** Override the default label. */
  readonly label?: string;
}

const TIER_STYLE: Record<TierBadgeTier, { box: string; dot: string; defaultLabel: string }> = {
  tier_1: {
    box: 'bg-surface-2 text-ink-2 border border-hairline-2',
    dot: 'bg-ink-3',
    defaultLabel: 'Tier 1 · Listed',
  },
  tier_2: {
    box: 'bg-accent-soft text-ink',
    dot: 'bg-accent',
    defaultLabel: 'Tier 2 · Claimed',
  },
  tier_3: {
    box: 'bg-success/15 text-ink',
    dot: 'bg-success',
    defaultLabel: 'Tier 3 · Premium',
  },
};

const SIZE: Record<TierBadgeSize, { box: string; dot: string }> = {
  sm: { box: 'h-5 px-2 gap-1.5 text-[10px]', dot: 'size-1.5' },
  md: { box: 'h-6 px-2.5 gap-2 text-[11px]', dot: 'size-2' },
};

/**
 * Venue tier pill — 1 listed / 2 claimed / 3 premium. The single source of truth
 * for tier styling across venue cards, detail headers, and admin queues.
 *
 * Composed from design-token colours only (no raw hex). Pairs with `OpenStatusPill`
 * in the F10 venue-detail title block and the F14 discovery `VenueCard`.
 */
export function TierBadge({ tier, size = 'md', label, className, ...rest }: TierBadgeProps) {
  const style = TIER_STYLE[tier];
  const sz = SIZE[size];
  const text = label ?? style.defaultLabel;
  return (
    <span
      role="img"
      aria-label={`Tier indicator: ${text}`}
      className={cn(
        'inline-flex items-center rounded-full font-bold uppercase tracking-wider whitespace-nowrap font-mono align-middle',
        style.box,
        sz.box,
        className,
      )}
      {...rest}
    >
      <span
        aria-hidden="true"
        className={cn('inline-block rounded-full shrink-0', sz.dot, style.dot)}
      />
      <span>{text}</span>
    </span>
  );
}
